import type { FC, ReactNode } from "react";
import { useState } from "react";
import { Field, FieldArray } from "redux-form";

import { Box, Button, Typography } from "@braintrust/braintrust-ui-components";
import { Loader } from "@braintrust/braintrust-ui-components";
import type { UpdateFreelancerProfileParams } from "@js/apps/freelancer/types";
import { createFormInstance } from "@js/forms/components";
import { GoogleComboboxField, TextField } from "@js/forms/fields";
import { required } from "@js/forms/validators";

import { ExternalProfilesField } from "../fields/external-profiles";

import styles from "./style.module.scss";

export type AccountInfoFormData = Pick<
  UpdateFreelancerProfileParams,
  "user" | "location_full" | "external_profiles"
>;

const AccountInfoFormInstance = createFormInstance<
  AccountInfoFormData,
  unknown
>("account-info-form-modal");

type AccountInfoFormProps = {
  onSubmit: (data: AccountInfoFormData) => void;
  onSubmitSuccess: () => void;
  initialValues: AccountInfoFormData;
  headerComponent?: ReactNode;
  hideExternalSites?: boolean;
};

export const AccountInfoForm: FC<AccountInfoFormProps> = ({
  headerComponent,
  hideExternalSites,
  ...props
}) => {
  return (
    <AccountInfoFormInstance {...props}>
      {({ submitting }) => {
        return (
          <AccountInfoFormComponent
            hideExternalSites={hideExternalSites}
            headerComponent={headerComponent}
            submitting={submitting}
          />
        );
      }}
    </AccountInfoFormInstance>
  );
};

type AccountInfoFormComponentProps = {
  headerComponent?: ReactNode;
  hideExternalSites?: boolean;
  submitting: boolean;
};
export const AccountInfoFormComponent = ({
  headerComponent,
  hideExternalSites,
  submitting,
}: AccountInfoFormComponentProps) => {
  const [isSaveReady, setIsSaveReady] = useState(true);

  return (
    <Box>
      {headerComponent}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography component="p" variant="paragraph" size="large">
          Let’s get started with some personal details
        </Typography>
        <div className={styles.bioFieldWrapper}>
          <Field
            id="first_name"
            required
            name="user.first_name"
            component={TextField}
            label="Legal first name"
            placeholder="Enter your first name"
            validate={[required]}
          />
          <Field
            id="last_name"
            required
            name="user.last_name"
            component={TextField}
            label="Legal last name"
            placeholder="Enter your last name"
            validate={[required]}
          />
          <Field
            id="location_full"
            name="location_full"
            className={styles.location}
            disableClearable
            required
            component={GoogleComboboxField}
            label="Location"
            onGetLocationStart={() => setIsSaveReady(false)}
            onGetLocationEnd={() => setIsSaveReady(true)}
            placeholder="Enter your city"
            validate={[required]}
          />
        </div>

        {!hideExternalSites && (
          <>
            <Typography
              component="p"
              variant="paragraph"
              size="large"
              sx={{ mt: 1 }}
            >
              Your online profiles
            </Typography>

            <FieldArray
              name="external_profiles"
              component={ExternalProfilesField}
            />
          </>
        )}
      </Box>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="positive"
          disabled={!isSaveReady}
          shape="squared"
          type="submit"
          sx={{ svg: { color: "var(--white)" } }}
          endIcon={submitting ? <Loader size={20} /> : undefined}
        >
          {submitting ? "Saving..." : "Save and continue"}
        </Button>
      </Box>
    </Box>
  );
};
